import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";

import Combobox from "@/components/Combobox";
import ResponseMessage from "@/components/Error/ResponseMessage";
import Icon from "@/components/Icon/Icon";
import { HStack, VStack } from "@/components/Layout";
import { SelectField } from "@/components/Select/SelectField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { WorkerType } from "@/constants/workerType";
import useValueToLabelCache from "@/hooks/useValueToLabelCache";
import { apiClient } from "@/lib/api/client";
import { getDataOrThrowApiError } from "@/lib/api/error";
import { getPath } from "@/lib/api/path";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { INFINITE_QUERY_KEY_SUFFIX } from "@/lib/queryClient";

import type { FormContextErrors } from "./-xstate/add-person-to-send-invitation-dialog.machine";

const AddPersonToSendInvitationDialogWorkerTypeFormItem = ({
  selectedWorkerType,
  setSelectedWorkerType,
  selectedWorkerTypeMaster,
  setSelectedWorkerTypeMaster,
  fixedWorkerType = false,
  errors,
}: {
  selectedWorkerType: WorkerType;
  setSelectedWorkerType: (value: WorkerType) => void;
  selectedWorkerTypeMaster: { id: string; name: string };
  setSelectedWorkerTypeMaster: (value: { id: string; name: string }) => void;
  errors: FormContextErrors;
  fixedWorkerType?: boolean;
}) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix:
      "pages.settings.persons.account.tabFilterBar.addPersonToSendInvitationDialog",
  });

  const WORKER_TYPE_OPTIONS = [
    { label: t("employee"), id: WorkerType.EMPLOYEE },
    { label: t("boardMember"), id: WorkerType.BOARD_MEMBER },
    { label: t("contractor"), id: WorkerType.CONTRACTOR },
    { label: t("collaborator"), id: WorkerType.COLLABORATOR },
  ];

  const WORKER_TYPE_MASTER_ID_LABELS = [
    { label: t("employeeMasterId"), id: WorkerType.EMPLOYEE },
    { label: t("boardMemberMasterId"), id: WorkerType.BOARD_MEMBER },
    { label: t("contractorMasterId"), id: WorkerType.CONTRACTOR },
    { label: t("collaboratorMasterId"), id: WorkerType.COLLABORATOR },
  ];

  const [freeText, setFreeText] = useState("");
  const [open, setOpen] = useState(false);
  const valueToLabelCache = useValueToLabelCache();

  const {
    data,
    isPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    enabled: open,
    queryKey: [
      getPath("/v2/worker-types-for-select"),
      { workerType: selectedWorkerType, freeText },
      INFINITE_QUERY_KEY_SUFFIX,
    ] as const,
    queryFn: async ({
      queryKey: [path, { workerType, freeText }],
      pageParam,
    }) =>
      await apiClient
        .GET(path, {
          params: { query: { kind: workerType, freeText, page: pageParam } },
        })
        .then((res) => getDataOrThrowApiError(res)),
    initialPageParam: 1,
    getNextPageParam: ({ page, maxPage }) =>
      page < maxPage ? page + 1 : undefined,
    select: (data) =>
      Object.fromEntries(
        data.pages.flatMap(({ items }) =>
          items.map((app) => [app.id, app.name]),
        ),
      ),
  });

  const workerTypeMasterIdLabel = WORKER_TYPE_MASTER_ID_LABELS.find(
    (option) => option.id === selectedWorkerType,
  );

  const handleValueChange = (value: string | null) => {
    if (!value) return;
    const selectedItemName = data?.[value];
    if (!selectedItemName) return;
    setSelectedWorkerTypeMaster({ id: value, name: selectedItemName });
    setOpen(false);
    setFreeText("");
  };

  return (
    <HStack className="justify-between">
      <VStack className="w-[180px] gap-1.5">
        <HStack className="gap-2 items-center h-4">
          <Label className="text-xs font-semibold text-neutral-muted-on-fill">
            {t("workerType")}
          </Label>
        </HStack>

        <SelectField
          value={selectedWorkerType}
          options={WORKER_TYPE_OPTIONS}
          placeholder="-"
          className="min-w-0 max-w-none w-full"
          disabled={fixedWorkerType}
          onValueChange={(value) => setSelectedWorkerType(value as WorkerType)}
        />

        {errors.workerType && (
          <ResponseMessage>
            {t(`validationErrors.${errors.workerType}`)}
          </ResponseMessage>
        )}
      </VStack>

      <VStack className="w-[180px] gap-1.5">
        <HStack className="gap-2 items-center h-4">
          <Label className="text-xs font-semibold text-neutral-muted-on-fill">
            {workerTypeMasterIdLabel?.label}
          </Label>
        </HStack>

        <Popover open={open} onOpenChange={setOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex justify-between items-center w-[180px] overflow-hidden text-ellipsis"
              disabled={isLoading}
            >
              <span className="truncate">{selectedWorkerTypeMaster.name}</span>
              <Icon
                iconName="ChevronsUpDownIcon"
                className="shrink-0 opacity-50 ml-2"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-popover-lg p-0 z-50">
            <Combobox
              type="single"
              options={Object.entries(data ?? {}).map(([id, label]) => ({
                id,
                label,
                value: id,
              }))}
              isFetching={isPending}
              value={selectedWorkerTypeMaster.id}
              onValueChange={handleValueChange}
              onSearchChange={setFreeText}
              onScrollToEnd={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
              valueToLabelCache={valueToLabelCache}
              selectionDisplayStyle="hidden"
            />
          </PopoverContent>
        </Popover>

        {errors.workerTypeMasterId && (
          <ResponseMessage>
            {t(`validationErrors.${errors.workerTypeMasterId}`, {
              workerTypeMasterId: workerTypeMasterIdLabel?.label,
            })}
          </ResponseMessage>
        )}
      </VStack>
    </HStack>
  );
};

export default AddPersonToSendInvitationDialogWorkerTypeFormItem;
